// External modules
import * as auth0 from 'auth0-js';
import { isEmpty } from 'lodash';
import axios from 'axios';

export default class Auth {
  constructor(config) {
    if (!config) return null;
    console.log('creating new Auth class');
    this.config = config;
    this.token = {};
  }

  getAuthConfig() {
    if (!this.config) return null;
    const { auth } = this.config;
    return auth;
  }

  getClientAudience(key) {
    const authConfig = this.getAuthConfig();
    if (!authConfig) return null;
    const clientAuthConfig = authConfig.client;
    if (!clientAuthConfig || !clientAuthConfig.audiences) return null;
    return clientAuthConfig.audiences[key];
  }

  getClientScopes(key) {
    const authConfig = this.getAuthConfig();
    if (!authConfig) return null;
    const clientAuthConfig = authConfig.client;
    if (!clientAuthConfig || !clientAuthConfig.scopes) return null;
    return clientAuthConfig.scopes[key];
  }

  getClientRealm(key) {
    const authConfig = this.getAuthConfig();
    if (!authConfig) return null;
    const clientAuthConfig = authConfig.client;
    if (!clientAuthConfig || !clientAuthConfig.scopes) return null;
    return clientAuthConfig.scopes[key];
  }

  getClientConfig(audienceKey) {
    const authConfig = this.getAuthConfig();
    if (!authConfig) return null;
    const clientAuthConfig = authConfig.client;
    if (!clientAuthConfig) return null;
    const audience = this.getClientAudience(audienceKey);
    if (!audience) return null;
    return {
      authClientAudience: audience,
      authClientGrantType: clientAuthConfig.grantType,
      authClientId: authConfig.id,
      authClientSecret: clientAuthConfig.secret,
      authClientURL: clientAuthConfig.url,
    };
  }

  async getToken(key) {
    if (!this.config || !key) return null;
    if (
      isEmpty(this.token) ||
      !this.token[key] ||
      !this.token[key].accessToken ||
      !this.token[key].tokenType
    ) {
      return await this.setNewClientToken(key);
    }

    return this.token[key];
  }

  // REF: https://auth0.com/docs/libraries/auth0js/v9
  getWebAuth(key) {
    const authConfig = this.getAuthConfig();
    if (!authConfig) return null;
    const webAuthConfig = authConfig.web;
    if (!webAuthConfig) return null;
    let cfg = {
      clientID: authConfig.id,
      domain: webAuthConfig.domain,
      redirectUri: webAuthConfig.redirectURL,
      responseType: webAuthConfig.responseType,
    };

    if (key) {
      const audience = this.getClientAudience(key);
      const scopes = this.getClientScopes(key);

      if (audience) {
        cfg.audience = audience;
      }

      if (scopes && !isEmpty(scopes)) {
        const scopesReducer = (accumulator, currentValue) => {
          return `${accumulator} ${currentValue}`;
        };

        const scopeText = scopes.reduce(scopesReducer);
        cfg.scope = scopeText;
      }
    }
    // console.log(cfg);

    return new auth0.WebAuth(cfg);
  }

  async setNewClientToken(key) {
    if (!key) return null;
    console.log('setting new client token for:', key);
    let clientConfig = this.getClientConfig(key);
    if (!clientConfig) return null;
    const clientAuth = new ACOauth2MachineClient(clientConfig);
    const token = await clientAuth.auth();

    if (token) {
      console.log('set new client token', token);
      this.token[key] = token;
      return token;
    }

    return null;
  }
}


class ACOauth2MachineClient {
  // url: string;
  // clientId: string;
  // clientSecret: string;
  // audience: string;
  // grantType: string;

  constructor(config) {
    this.url = config.authClientURL;
    this.clientId = config.authClientId;
    this.clientSecret = config.authClientSecret;
    this.audience = config.authClientAudience;
    this.grantType = config.authClientGrantType;
  }

  async auth() {
    try {
      const authReqBody = {
        client_id: this.clientId,
        client_secret: this.clientSecret,
        audience: this.audience,
        grant_type: this.grantType,
      };
      const authReq = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      const authRes = await axios.post(this.url, JSON.stringify(authReqBody), authReq);

      if (authRes.status !== 200) {
        throw new ACError(`Failed authenticating to Auth0 ${authRes.status} ${authRes.statusText}`);
      }

      if (authRes.data) {
        return {
          accessToken: authRes.data.access_token,
          tokenType: authRes.data.token_type,
          expirySecond: authRes.data.expires_in,
          expiryInSeconds: authRes.data.expires_in,
          scope: authRes.data.scope,
        };
      }
    } catch (error) {
      let result = error;
      console.error('Error ACOauth2MachineClient:auth', error);

      if (error instanceof ACError === false) {
        result = new ACError(error.message);
      }
      return Promise.reject(result);
    }
  }
}


class ACError extends Error {
  constructor(message, code, type, isIgnorable) {
      super(message);
      this.name = 'ACError';
      this.code = code;
      this.httpStatusCode = code;
      this.type = type;
      this.isIgnoreable = isIgnorable || false;
  }
}